import React, { useState } from 'react';
import { Card, Button, InputGroup, Form as BtForm, Row, Col, ProgressBar } from 'react-bootstrap';
import { LuMail, LuLock, LuUser, LuEye, LuEyeOff, LuCheck, LuX } from "react-icons/lu";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from "yup";
import { Formik, Form, ErrorMessage } from "formik";
import { useDispatch } from 'react-redux';
import { signUp, createSubscriptionCheckout } from '../../redux/reducers/authSlice';

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [searchParams] = useSearchParams();
    const tierFromUrl = searchParams.get('tier') || 'free';

    const initialValues = {
        fullName: "",
        email: "",
        password: "",
        confirmPassword: "",
        subscriptionTier: ['free', 'premium', 'enterprise'].includes(tierFromUrl) ? tierFromUrl : 'free',
        agreeTerms: false
    };

    const validationSchema = Yup.object({
        fullName: Yup.string().required("Full Name is required"),
        email: Yup.string()
            .email("Please enter a valid email address")
            .required("Email is required"),
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            .matches(/[A-Z]/, "Requires one uppercase letter")
            .matches(/[0-9]/, "Requires one number")
            .matches(/[!@#$%^&*]/, "Requires one special character")
            .required("Password is required"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], "Passwords must match")
            .required("Confirm Password is required"),
        subscriptionTier: Yup.string().oneOf(
            ['free', 'premium', 'enterprise'],
            "Invalid Tier"
        ).required("Subscription tier is required"),
        agreeTerms: Yup.boolean()
            .oneOf([true], "You must accept the terms and conditions")
    });

    const getPasswordStrength = (pw) => {
        return {
            length: pw.length >= 8,
            capital: /[A-Z]/.test(pw),
            number: /[0-9]/.test(pw),
            special: /[!@#$%^&*]/.test(pw)
        };
    };

    const onSubmit = (values, { setSubmitting }) => {
        const { fullName, email, password, subscriptionTier } = values;

        dispatch(signUp({ fullName, email, password, subscriptionTier })).then(async (res) => {
            const responseData = res.payload;

            if (responseData && responseData.token) {
                localStorage.setItem("token", responseData.token);
                localStorage.setItem("masterCustomerId", responseData.user.id);

                if (responseData.pendingTier) {
                    try {
                        const checkoutRes = await dispatch(createSubscriptionCheckout(responseData.pendingTier)).unwrap();
                        if (checkoutRes && checkoutRes.checkoutUrl) {
                            window.location.href = checkoutRes.checkoutUrl;
                            return;
                        }
                    } catch (err) {
                        console.error("Failed to initiate checkout:", err);
                    }
                }

                navigate("/onboarding/verify-notice");
            }
            // If it fails, toast handles the error and the user stays on the form
        }).finally(() => {
            setSubmitting(false);
        });
    };

    return (
        <div className="auth-wrapper d-flex align-items-center justify-content-center bg-light px-3" style={{ minHeight: '100vh' }}>
            <Card className="auth-card border-0 shadow-lg rounded-4 overflow-visible position-relative p-3 p-md-4" style={{ maxWidth: '480px', width: '100%' }}>

                <div className="auth-logo-badge position-absolute top-0 start-50 translate-middle">
                    <div className="bg-brand rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                        S
                    </div>
                </div>

                <div className="text-center mt-4 mb-4">
                    <h3 className="fw-bold text-dark">Create Account</h3>
                    <p className="text-muted small px-3">
                        Join Seven's Biz and start managing your
                        <br />Invoices efficiently today.
                    </p>
                </div>

                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                    {(formik) => {
                        const strength = getPasswordStrength(formik.values.password);
                        const strengthCount = Object.values(strength).filter(Boolean).length;

                        return (
                            <Form>
                                {/* Full Name Field */}
                                <BtForm.Group className="mb-3">
                                    <BtForm.Label className="small fw-bold">Full Name</BtForm.Label>
                                    <InputGroup className={`bg-light rounded-3 border ${formik.errors.fullName && formik.touched.fullName ? 'border-danger' : ''}`}>
                                        <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                            <LuUser size={18} />
                                        </InputGroup.Text>
                                        <BtForm.Control
                                            type="text"
                                            placeholder="John Doe"
                                            className="bg-transparent border-0 py-2 shadow-none"
                                            {...formik.getFieldProps('fullName')}
                                        />
                                    </InputGroup>
                                    <ErrorMessage name="fullName" component="div" className="text-danger small mt-1" />
                                </BtForm.Group>

                                {/* Email Address Field */}
                                <BtForm.Group className="mb-3">
                                    <BtForm.Label className="small fw-bold">Email Address</BtForm.Label>
                                    <InputGroup className={`bg-light rounded-3 border ${formik.errors.email && formik.touched.email ? 'border-danger' : ''}`}>
                                        <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                            <LuMail size={18} />
                                        </InputGroup.Text>
                                        <BtForm.Control
                                            type="email"
                                            placeholder="john@example.com"
                                            className="bg-transparent border-0 py-2 shadow-none"
                                            {...formik.getFieldProps('email')}
                                        />
                                    </InputGroup>
                                    <ErrorMessage name="email" component="div" className="text-danger small mt-1" />
                                </BtForm.Group>

                                {/* Password Field */}
                                <BtForm.Group className="mb-3">
                                    <BtForm.Label className="small fw-bold">Password</BtForm.Label>
                                    <InputGroup className={`bg-light rounded-3 border ${formik.errors.password && formik.touched.password ? 'border-danger' : ''}`}>
                                        <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                            <LuLock size={18} />
                                        </InputGroup.Text>
                                        <BtForm.Control
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            className="bg-transparent border-0 py-2 shadow-none"
                                            {...formik.getFieldProps('password')}
                                        />
                                        <Button variant="link" className="text-muted border-0 shadow-none" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                                        </Button>
                                    </InputGroup>

                                    {/* Real-time Strength Meter */}
                                    <div className="mt-3 p-2 rounded bg-white border border-light shadow-sm">
                                        <Row className="g-2 mb-2">
                                            <StrengthItem label="8+ Chars" active={strength.length} />
                                            <StrengthItem label="1 Capital" active={strength.capital} />
                                            <StrengthItem label="1 Number" active={strength.number} />
                                            <StrengthItem label="1 Special" active={strength.special} />
                                        </Row>
                                        <ProgressBar
                                            now={(strengthCount / 4) * 100}
                                            variant={strengthCount === 4 ? "success" : strengthCount >= 2 ? "warning" : "danger"}
                                            style={{ height: '6px' }}
                                        />
                                    </div>
                                    <ErrorMessage name="password" component="div" className="text-danger small mt-1" />
                                </BtForm.Group>

                                {/* Confirm Password Field */}
                                <BtForm.Group className="mb-4">
                                    <BtForm.Label className="small fw-bold">Confirm Password</BtForm.Label>
                                    <InputGroup className={`bg-light rounded-3 border ${formik.errors.confirmPassword && formik.touched.confirmPassword ? 'border-danger' : ''}`}>
                                        <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                            <LuLock size={18} />
                                        </InputGroup.Text>
                                        <BtForm.Control
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            className="bg-transparent border-0 py-2 shadow-none"
                                            {...formik.getFieldProps('confirmPassword')}
                                        />
                                        <Button variant="link" className="text-muted border-0 shadow-none" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                                        </Button>
                                    </InputGroup>
                                    <ErrorMessage name="confirmPassword" component="div" className="text-danger small mt-1" />
                                </BtForm.Group>

                                {/* Subscription Tier Field */}
                                <BtForm.Group className="mb-4">
                                    <BtForm.Label className="small fw-bold">Select Subscription Tier</BtForm.Label>
                                    <BtForm.Select
                                        className={`bg-light border shadow-none py-2 rounded-3 ${formik.errors.subscriptionTier && formik.touched.subscriptionTier ? 'border-danger' : ''}`}
                                        {...formik.getFieldProps('subscriptionTier')}
                                    >
                                        <option value="free">Free - Basic Access</option>
                                        <option value="premium">Premium - Advanced Features</option>
                                        <option value="enterprise">Enterprise - Unlimited Business</option>
                                    </BtForm.Select>
                                    <ErrorMessage name="subscriptionTier" component="div" className="text-danger small mt-1" />
                                </BtForm.Group>

                                <BtForm.Group className="mb-4 text-start">
                                    <BtForm.Check
                                        type="checkbox"
                                        id="agreeTerms"
                                        name="agreeTerms"
                                        {...formik.getFieldProps('agreeTerms')}
                                        checked={formik.values.agreeTerms}
                                        label={
                                            <span className="small text-muted">
                                                I agree to all the <Link to="/terms" className="text-brand text-decoration-none fw-bold">Terms &amp; Conditions</Link> and <Link to="/fees" className="text-brand text-decoration-none fw-bold">Fees</Link>.
                                            </span>
                                        }
                                        isInvalid={!!formik.errors.agreeTerms && formik.touched.agreeTerms}
                                    />
                                    <ErrorMessage name="agreeTerms" component="div" className="text-danger small" />
                                </BtForm.Group>

                                <Button variant="brand" type="submit" className="btn btn-primary w-100 py-2 fw-bold mb-4 shadow-sm" disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? 'CREATING ACCOUNT...' : 'REGISTER'}
                                </Button>

                                <p className="text-center text-muted small mb-0">
                                    Already have an account? <Link to="/login" className="text-brand text-decoration-none fw-bold">Login Here</Link>
                                </p>
                            </Form>
                        );
                    }}
                </Formik>
            </Card>
        </div>
    );
};

const StrengthItem = ({ label, active }) => (
    <Col xs={6}>
        <div className={`d-flex align-items-center small ${active ? 'text-success fw-bold' : 'text-muted opacity-50'}`}>
            {active ? <LuCheck size={14} className="me-1" /> : <LuX size={14} className="me-1" />}
            {label}
        </div>
    </Col>
);

export default Register;
